/**
 * Episode Selector View for Evaluation
 *
 * Landing page for the "Improve Episode" tab. Shows a filterable table of episodes
 * that links to the episode eval page. Uses the global language dropdown from
 * EpisodeNavigation to filter episodes.
 *
 * Story: 24.1 - Lab Foundation & Eval Personas
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEpisodes, type EpisodeListItem, type EpisodeStatus } from '../services/episodesApi';
import { useLanguage } from 'contexts/LanguageContext';
import { useInitialData } from 'contexts/InitialDataContext';
import { getSkillLevelEnglishName } from 'services/initialDataApi';
import { EpisodeNavigation } from '../components/EpisodeNavigation';

export function EpisodeSelectorView() {
  const navigate = useNavigate();
  const { selectedLanguage } = useLanguage();
  const { state: initialDataState } = useInitialData();
  const skillLevels = initialDataState.data?.skillLevels || [];

  const [statusFilter, setStatusFilter] = useState<EpisodeStatus | ''>('');
  const [complexityFilter, setComplexityFilter] = useState<number | ''>('');

  const filters = {
    languageCode: selectedLanguage,
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(complexityFilter ? { complexity: [complexityFilter] } : {}),
  };

  const { data: episodes, isLoading } = useEpisodes(filters);

  const getCefrCode = (complexityLevel: number) => {
    const sl = skillLevels.find((s) => s.id === complexityLevel);
    return sl?.cefr_code || `L${complexityLevel}`;
  };

  const getSkillName = (complexityLevel: number) => {
    const sl = skillLevels.find((s) => s.id === complexityLevel);
    return sl ? getSkillLevelEnglishName(sl) : `Level ${complexityLevel}`;
  };

  const handleSelectEpisode = (episode: EpisodeListItem) => {
    navigate(`/episodes/eval/${episode.id}`);
  };

  return (
    <div>
      <EpisodeNavigation />

      <h1 className="text-2xl font-bold text-gray-900 mb-6">Select Episode for Evaluation</h1>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Complexity</label>
          <select
            value={complexityFilter}
            onChange={(e) => setComplexityFilter(e.target.value ? Number(e.target.value) : '')}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white min-w-[160px]"
          >
            <option value="">All Levels</option>
            {skillLevels.map((sl) => (
              <option key={sl.id} value={sl.id}>
                {sl.cefr_code} — {getSkillLevelEnglishName(sl)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as EpisodeStatus | '')}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white min-w-[160px]"
          >
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="deactivated">Deactivated</option>
          </select>
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      )}

      {/* Table */}
      {!isLoading && episodes && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Language
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Complexity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {episodes.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                    No episodes match the selected filters.
                  </td>
                </tr>
              ) : (
                episodes.map((episode) => (
                  <tr
                    key={episode.id}
                    onClick={() => handleSelectEpisode(episode)}
                    className="hover:bg-blue-50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{episode.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {episode.languageCode.toUpperCase()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      <span className="inline-flex items-center gap-1">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                          {getCefrCode(episode.complexityLevel)}
                        </span>
                        <span className="text-gray-500">
                          {getSkillName(episode.complexityLevel)}
                        </span>
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span
                        className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                          episode.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : episode.status === 'draft'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {episode.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
